import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postImpFree, postOrder } from 'src/services/checkout';
import IamPort from 'src/services/iamport';
import PromotionChart from 'src/components/PromotionChart';
import useCustomHistory from 'src/hooks/useCustomHistory';
import PaymentPayButton from '../PayButton';
import { usePromotionCode } from '../PromotionCode';
import PaymentEmail from '../Email';
import { usePaymentAgree } from '../Agree';
import PaymentReceipt from '../Receipt';
import PaymentReceiptSubscription from '../ReceiptSubscription';
import { useModal } from 'src/ModalContext';
import Badge from 'src/components/Badge';
import { isKorean } from 'src/services/util';
import StickyButton from 'src/components/StickyButton';

const iamport = new IamPort();

export default function PaymentIamport({
  product,
  payMethod,
  isUsed,
}: {
  product: CheckoutProductData;
  payMethod: string;
  isUsed: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const { errorModal } = useModal();
  const history = useCustomHistory();
  const [isEnablePay, setIsEnablePay] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState([] as IamPortPaymentOption[]);
  const [paymentOption, setPaymentOption] = useState({} as IamPortPaymentOption);

  const { promotion, PromotionCode } = usePromotionCode({
    product,
  });

  const { checked, PaymentAgree } = usePaymentAgree();

  useEffect(() => {
    if (product.id) {
      (async function load() {
        try {
          await iamport.init(product);
        } catch (e) {
          errorModal(e);
        }
        setPaymentOptions(iamport.getPaymentOptions());
        if (payMethod) {
          const paymentOptionIndex = iamport.getPaymentOptions().findIndex((option) => option.payMethod === payMethod);
          setPaymentOption(iamport.getPaymentOptions()[paymentOptionIndex > -1 ? paymentOptionIndex : 0]);
        } else {
          setPaymentOption(iamport.getPaymentOptions()[0]);
        }
      })();
    }
    return () => {
      iamport.destroy();
    };
  }, [product]);

  const onBuyItem = useCallback(async () => {
    try {
      const result = await iamport.buyItem(paymentOption);
      history.push('/payment/complete', {
        ...result,
        imp_success: 'true',
      });
    } catch (e) {
      history.push('/payment/fail', {
        productId: product.id,
        error_msg: e.message,
        payMethod: paymentOption.payMethod,
      });
    }
  }, [paymentOption, promotion, product]);

  const onBuyFreeItem = useCallback(async () => {
    try {
      const order = await postOrder({
        productId: product.id,
        ...(promotion
          ? {
              promotionId: promotion.id,
            }
          : {}),
      });

      await postImpFree({
        orderId: order.order.id,
        promotionCode: promotion.code,
      });

      history.push('/payment/complete', {
        orderId: order.order.id,
        productId: product.id,
        imp_success: 'true',
      });
    } catch (e) {
      history.push('/payment/fail', {
        productId: product.id,
        error_msg: e.message,
        payMethod: paymentOption.payMethod,
      });
    }
  }, [product, promotion]);

  useEffect(() => {
    iamport.setPromotionCode(promotion);
  }, [promotion.id]);

  useEffect(() => {
    if (product.subscription) {
      setIsEnablePay(checked);
    } else {
      setIsEnablePay(paymentOption.payMethod ? true : false);
    }
  }, [product, checked, paymentOption]);

  console.log(isUsed);
  return (
    <>
      <div className="container mt-6">
        {product.subscription && (
          <div className="mb-3">
            <Badge color="light-grey-blue" title={t('payment/complete/badge_billing')}></Badge>
          </div>
        )}
        <h1 className="N_20_R text-black">{isKorean() ? product.koName : product.enName}</h1>
      </div>
      <div className="mt-6 divide-line"></div>
      <div className="container">
        {!isUsed && product.subscription && product.promotionCycle > 0 && <PromotionChart product={product} />}
        <div className="mt-8">{t('payment/payment_option_title')}</div>
        <div className="mt-2">
          {paymentOptions.map((option) => (
            <div className="mt-2" key={option.payMethod}>
              <PaymentPayButton
                title={t(`payment/payment_option_${option.payMethod}`)}
                selected={option.payMethod === paymentOption.payMethod}
                onClick={() => setPaymentOption(option)}
              ></PaymentPayButton>
            </div>
          ))}
        </div>
        {!product.subscription && PromotionCode}
        <PaymentEmail />
      </div>
      {product.subscription && PaymentAgree}
      {product.subscription ? (
        <PaymentReceiptSubscription product={product} isUsed={isUsed} />
      ) : (
        <PaymentReceipt product={product} promotion={promotion} />
      )}
      <div className="mt-10 pt-6 pb-10 bg-pale-grey">
        <ul className="container list-disc N_12_R text-brown-grey">
          <li>{t('payment/disclaimer_1')}</li>
          <li>{t('payment/disclaimer_2')}</li>
        </ul>
      </div>
      <div className="sticky bottom-0 w-full">
        <div className="bg-bluey-purple">
          {!product.subscription && promotion.saleAmount && product.amount - promotion.saleAmount === 0 ? (
            <StickyButton title={t('payment/purchase_button_title')} onClick={onBuyFreeItem} disabled={!isEnablePay}></StickyButton>
          ) : (
            <StickyButton title={t('payment/purchase_button_title')} onClick={onBuyItem} disabled={!isEnablePay}></StickyButton>
          )}
        </div>
      </div>
    </>
  );
}
